// @givmo/app-admin — client telemetry (Sentry + GA4) singletons.
//
// Day-one telemetry, mirroring the donate app's init but reading from the SPA env (src/lib/env.ts)
// rather than the SSR `window.ENV`. Both packages are SSR-safe no-ops until a supported browser
// attaches; here they run on the client only. PII-safety is enforced by the packages themselves (the
// analytics PII deny-list scrub + the Sentry beforeSend scrubber) — this module never passes PII in.
//
// NOTE (parity carry-forward): the Flutter webapp had NO analytics anywhere. doc 07 §7 ("telemetry
// day-one") deliberately raises that bar for the rebuild — Sentry + GA4 are wired from the scaffold.

import { createAnalytics, type GivmoAnalytics } from '@givmo/analytics';
import { initObservability } from '@givmo/observability';
import type { AdminEnv } from './env';
import { ADMIN_CLIENT_PLATFORM } from './constants';

let analytics: GivmoAnalytics | null = null;
let observabilityStarted = false;

/** Initialize Sentry once (no-op without a DSN). Forced PII scrubbing is owned by the package. */
export function initClientObservability(env: AdminEnv): void {
  if (observabilityStarted) return;
  observabilityStarted = true;
  initObservability({
    dsn: env.sentryDsn,
    environment: env.deployEnv,
    clientIdentity: { platform: ADMIN_CLIENT_PLATFORM, version: env.appVersion },
  });
}

/** Lazily create the GA4 analytics handle from the env Firebase config. */
export function getAnalytics(env: AdminEnv): GivmoAnalytics | null {
  if (analytics !== null) return analytics;
  analytics = createAnalytics({
    firebaseConfig: {
      apiKey: env.firebase.apiKey,
      authDomain: env.firebase.authDomain,
      projectId: env.firebase.projectId,
      appId: env.firebase.appId,
      storageBucket: env.firebase.storageBucket,
      messagingSenderId: env.firebase.messagingSenderId,
      measurementId: env.firebase.measurementId,
    },
  });
  return analytics;
}

/** Reset telemetry singletons — test-only seam. */
export function resetTelemetryForTests(): void {
  analytics = null;
  observabilityStarted = false;
}
